import { VENUS_FLUX_URL } from 'constants/production';
import { routes } from 'constants/routing';
import { useGetMarketsPagePath } from 'hooks/useGetMarketsPagePath';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { useTranslation } from 'libs/translations';
import { useAccountAddress } from 'libs/wallet';
import type { MenuItem, SubMenu } from '../types';
import forgeCoreIconSrc from './forgeCoreIcon.png';
import forgeFluxIconSrc from './forgeFluxIcon.png';

const SHOW_MARKETS_DROPDOWN = false;

export const useMenuItems = () => {
  const { t } = useTranslation();
  const { accountAddress } = useAccountAddress();
  const swapRouteEnabled = useIsFeatureEnabled({ name: 'swapRoute' });
  // const bittensorSwapRouteEnabled = useIsFeatureEnabled({ name: 'bittensorSwapRoute' });
  const vaiRouteEnabled = useIsFeatureEnabled({ name: 'vaiRoute' });
  const bridgeRouteEnabled = useIsFeatureEnabled({ name: 'bridgeRoute' });
  const statsRouteEnabled = useIsFeatureEnabled({ name: 'statsRoute' });
  const tradeRouteEnabled = useIsFeatureEnabled({ name: 'trade' });
  const isolatedPoolsRouteEnabled = useIsFeatureEnabled({ name: 'isolatedPoolsRoute' });
  const vaultsRouteEnabled = useIsFeatureEnabled({ name: 'vaultsRoute' });
  const governanceRouteEnabled = useIsFeatureEnabled({ name: 'governanceRoute' });
  const portRouteEnabled = useIsFeatureEnabled({ name: 'portRoute' });
  const devnetFaucetRouteEnabled = useIsFeatureEnabled({ name: 'devnetFaucetRoute' });
  const { marketsPagePath } = useGetMarketsPagePath();

  const menu: Array<MenuItem | SubMenu> = [];

  menu.push({
    to: routes.dashboard.path,
    label: t('layout.menu.dashboard.label'),
  });

  if (SHOW_MARKETS_DROPDOWN) {
    menu.push({
      label: t('layout.menu.markets.label'),
      to: marketsPagePath,
      variant: 'secondary',
      items: [
        {
          to: marketsPagePath,
          imgSrc: forgeCoreIconSrc,
          label: t('layouts.menu.markets.forgeCore.label'),
          description: t('layouts.menu.markets.forgeCore.description'),
        },
        ...(isolatedPoolsRouteEnabled
          ? [
              {
                href: VENUS_FLUX_URL,
                imgSrc: forgeFluxIconSrc,
                label: t('layouts.menu.markets.forgeFlux.label'),
                description: t('layouts.menu.markets.forgeFlux.description'),
              },
            ]
          : []),
      ],
    });
  } else {
    menu.push({
      to: marketsPagePath,
      label: t('layout.menu.markets.label'),
    });
  }

  if (vaultsRouteEnabled) {
    menu.push({
      to: routes.vaults.path,
      label: t('layout.menu.vaults.label'),
    });
  }

  if (devnetFaucetRouteEnabled) {
    menu.push({
      to: routes.devnetFaucet.path,
      label: t('layout.menu.devnetFaucet.label'),
    });
  }

  const othersSubMenuItems: MenuItem[] = [];

  if (governanceRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.governance.path,
      iconName: 'market',
      label: t('layout.menu.others.governance.label'),
      description: t('layout.menu.others.governance.description'),
    });
  }

  if (vaiRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.vai.path,
      iconName: 'vaiOutline',
      label: t('layout.menu.others.vai.label'),
      description: t('layout.menu.others.vai.description'),
    });
  }

  if (bridgeRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.bridge.path,
      iconName: 'bridge',
      label: t('layout.menu.others.bridge.label'),
      description: t('layout.menu.others.bridge.description'),
    });
  }

  if (accountAddress && portRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.port.path,
      iconName: 'download',
      label: t('layout.menu.others.port.label'),
      description: t('layout.menu.others.port.description'),
    });
  }

  if (tradeRouteEnabled) {
    menu.push({
      to: routes.trade.path,
      label: t('layout.menu.others.trade.label'),
      isBeta: true,
    });
  }

  if (swapRouteEnabled) {
    menu.push({
      to: routes.swap.path,
      label: t('layout.menu.others.swap.label'),
    });
  }

  // if (bittensorSwapRouteEnabled) {
  //   menu.push({
  //     to: routes.bittensorSwap.path,
  //     label: t('layout.menu.others.swap.label'),
  //     isBeta: true,
  //   });
  // }

  if (statsRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.stats.path,
      iconName: 'stats',
      label: t('layout.menu.others.stats.label'),
      description: t('layout.menu.others.stats.description'),
    });
  }

  if (othersSubMenuItems.length > 0) {
    menu.push({
      label: t('layout.menu.others.label'),
      items: othersSubMenuItems,
    });
  }

  return menu;
};
